// src/pages/ProductDetail.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';

function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const { addToCart } = useCart();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetch(`${API_BASE_URL}/products/${id}`)
      .then(res => res.json())
      .then(data => {
        setProduct(data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [API_BASE_URL, id]);

  const handleAdd = async () => {
    const result = await addToCart(product);
    if (result && result.success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } else {
      alert(result?.message || 'Out of stock / limit reached');
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] mt-20">
      <i className="bx bx-loader-alt animate-spin text-4xl text-green-700" />
      <p className="mt-3 text-slate-500 font-semibold font-[Poppins]">Loading fresh details...</p>
    </div>
  );
  if (!product) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] mt-20">
      <i className="bx bx-error-circle text-4xl text-red-600" />
      <p className="mt-3 text-red-600 font-bold font-[Poppins]">Product not found</p>
    </div>
  );

  return (
    <div className="product-detail-container mt-24 mb-16">
      
      {/* Product Image with floating effect */}
      <motion.div 
        className="product-image-card relative"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.img
          src={product.image}
          alt={product.name}
          whileHover={{ scale: 1.04 }}
          transition={{ type: "spring", stiffness: 180 }}
        />

        {/* Added status popover */}
        {added && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute bg-green-800/90 text-white backdrop-blur-md px-5 py-2.5 rounded-2xl shadow-xl font-bold flex items-center gap-2 text-sm border border-white/10"
          >
            <i className="bx bx-check-circle text-lg" />
            <span>Added to Cart!</span>
          </motion.div>
        )}
      </motion.div>

      {/* Product Info */}
      <motion.div 
        className="product-info-column space-y-5"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <div className="space-y-2">
          {/* Breadcrumb back to shop */}
          <a href="/products" className="detail-back-link">
            <i className="bx bx-left-arrow-alt" /> Back to Shop
          </a>
          
          <h1 className="text-3xl sm:text-4xl font-black text-slate-800 font-[Montserrat] leading-tight">
            {product.name}
          </h1>
          
          <p className="text-green-700 text-3xl font-black font-[Poppins]">
            ${Number(product.price).toFixed(2)}
          </p>
        </div>
        
        {/* Badges */}
        <div className="flex gap-2">
          <span className="badge-tag bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full border border-green-100">
            Fresh Pick
          </span>
          <span className={`badge-tag px-3.5 py-1.5 rounded-full border ${
            product.stock > 0 
              ? 'bg-amber-50 text-amber-700 border-amber-100' 
              : 'bg-red-50 text-red-700 border-red-100'
          }`}>
            {product.stock > 0 ? 'In Stock' : 'Sold Out'}
          </span>
        </div>

        {/* Specs Table */}
        <table className="specs-table">
          <tbody>
            <tr>
              <td className="label-col">Category</td>
              <td>{product.category || "Authentic Grocery"}</td>
            </tr>
            <tr>
              <td className="label-col">Weight / Size</td>
              <td>{product.description || "Per weight estimation"}</td>
            </tr>
            <tr>
              <td className="label-col">Availability</td>
              <td>{product.stock > 0 ? `${product.stock} items remaining` : 'Temporarily Unavailable'}</td>
            </tr>
          </tbody>
        </table>

        {/* Action Buttons */}
        <div className="pt-4">
          <motion.button
            whileTap={product.stock > 0 ? { scale: 0.96 } : {}}
            whileHover={product.stock > 0 ? { scale: 1.02 } : {}}
            className={`detail-cta-btn w-full sm:w-2/3 ${
              product.stock === 0 
                ? 'opacity-50 cursor-not-allowed bg-slate-200 text-slate-500 shadow-none' 
                : ''
            }`}
            onClick={() => product.stock > 0 && handleAdd()}
            disabled={product.stock === 0}
          >
            <i className="bx bx-cart text-xl"></i> 
            <span>{product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}</span>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}

export default ProductDetail;
